import {normalizeBasePath, normalizePathname} from '@/lib/routing'
import type {SiteLite} from '@/multisite/types'

const trimTrailingSlash = (value: string) => (value.length > 1 ? value.replace(/\/+$/, '') : value)

export const getPrimaryDomain = (site?: SiteLite): string | undefined => {
  if (!site) return undefined
  return (
    site.routing.domains.find((domain) => domain.isPrimary)?.host ||
    site.routing.domains[0]?.host ||
    undefined
  )
}

export const applyTrailingSlashPolicy = (
  pathname: string,
  policy: SiteLite['routing']['trailingSlashPolicy'] = 'ignore',
): string => {
  const normalized = normalizePathname(pathname)

  if (normalized === '/') {
    return '/'
  }

  if (policy === 'always') {
    return normalized.endsWith('/') ? normalized : `${normalized}/`
  }

  return trimTrailingSlash(normalized)
}

export const buildSitePath = (site: Pick<SiteLite, 'routing'>, routePath: string = '/'): string => {
  const basePath = normalizeBasePath(site.routing.basePath, {
    rootIsEmpty: true,
  })
  const normalizedRoutePath = normalizePathname(routePath)
  const pathAlreadyIncludesBasePath =
    basePath && (normalizedRoutePath === basePath || normalizedRoutePath.startsWith(`${basePath}/`))
  const fullPath =
    normalizedRoutePath === '/'
      ? basePath || '/'
      : pathAlreadyIncludesBasePath
        ? normalizedRoutePath
        : `${basePath}${normalizedRoutePath}`

  return applyTrailingSlashPolicy(fullPath || '/', site.routing.trailingSlashPolicy)
}

export const buildCanonicalUrl = (
  site: SiteLite,
  routePath: string = '/',
  options?: {originOverride?: string},
): string | undefined => {
  const originOverride = options?.originOverride?.replace(/\/+$/, '')
  if (originOverride) {
    return `${originOverride}${buildSitePath(site, routePath)}`
  }

  const host = getPrimaryDomain(site)
  if (!host) return undefined
  return `https://${host}${buildSitePath(site, routePath)}`
}

export const routePathFromDocumentSlug = (site: SiteLite, slug?: string | null): string | null => {
  if (!slug) return null

  const normalizedSlug = slug.replace(/^\/+|\/+$/g, '')
  if (!normalizedSlug) return '/'

  const basePath = normalizeBasePath(site.routing.basePath, {
    rootIsEmpty: true,
  }).replace(/^\/+/, '')

  const routeSlug =
    basePath && normalizedSlug.startsWith(`${basePath}/`)
      ? normalizedSlug.slice(basePath.length + 1)
      : normalizedSlug === basePath
        ? ''
        : normalizedSlug

  return routeSlug ? `/${routeSlug}` : '/'
}
